import React from 'react';
import {
  DirectoryChecklistIcon,
  DirectoryCompendiumIcon,
  DirectoryKnowledgeIcon,
  ItemBinderIcon,
  ItemChecklistIcon,
  ItemCompendiumIcon,
  SectionKnowledgeIcon,
  SectionChecklistIcon,
  SectionCompendiumIcon,
} from 'src/images';
import styles from './category-search-item.module.scss';
import { getExternalSyncBinderIcon } from 'src/constants/external-sync-icons-mapper';
import { CATEGORY_TYPES, ROUTES } from 'shared/constants';
import classNames from 'classnames';
import { LabelsList } from 'src/components/labels';
import { SingleTagIcon } from 'src/components/tags/tagsIcons';
import { useNavigate } from 'react-router-dom';
import { isSection } from 'shared/utils/category.utils';
import { getParentIdByPath } from 'shared/utils/Utils';

const CATEGORY_TYPES_TO_ICON_MAPPER = {
  [CATEGORY_TYPES.KNOWLEDGE]: {
    icon: ItemBinderIcon,
    folderIcon: DirectoryKnowledgeIcon,
    sectionIcon: SectionKnowledgeIcon,
  },
  [CATEGORY_TYPES.CHECKS]: {
    icon: ItemChecklistIcon,
    folderIcon: DirectoryChecklistIcon,
    sectionIcon: SectionChecklistIcon,
  },
  [CATEGORY_TYPES.VALUES]: {
    icon: ItemCompendiumIcon,
    folderIcon: DirectoryCompendiumIcon,
    sectionIcon: SectionCompendiumIcon,
  },
};

const getCategoryType = item => {
  let type;
  if (item.checks) {
    type = CATEGORY_TYPES.CHECKS;
  } else if (item.values) {
    type = CATEGORY_TYPES.VALUES;
  } else {
    type = CATEGORY_TYPES.KNOWLEDGE;
  }
  return {
    type,
    isCategory: item.type !== type,
  };
};

const CategorySearchItem = ({
  item,
  onClickEvent = () => {},
  disabled = false,
  selected = false,
  showLabels = false,
  light = true,
  showSpecialty = true,
  knowledgeSelectItem = () => {},
  labSelectValues = () => {},
  adminMode = false,
}) => {
  const navigate = useNavigate();
  const { title, subtitle, labels, tags, pathParts = [], options } = item;
  const { type: categoryType, isCategory } = getCategoryType(item);

  const externalSyncBinderIcon =
    !isCategory && adminMode ? getExternalSyncBinderIcon(options?.externalSyncProvider) : null;

  const Icon =
    externalSyncBinderIcon ??
    (!isCategory
      ? CATEGORY_TYPES_TO_ICON_MAPPER[categoryType].icon
      : isSection(pathParts)
        ? CATEGORY_TYPES_TO_ICON_MAPPER[categoryType].sectionIcon
        : CATEGORY_TYPES_TO_ICON_MAPPER[categoryType].folderIcon);

  const handleClick = () => {
    if (disabled) return;

    const event = {
      id: item.id,
      title,
      categoryType,
      isCategory,
      ...item,
    };

    onClickEvent(event);

    if (!isCategory) {
      const binderActions = {
        [CATEGORY_TYPES.KNOWLEDGE]: () => knowledgeSelectItem(item.id),
        [CATEGORY_TYPES.VALUES]: () => labSelectValues(item.id),
        // Note: admission doesn't have a binder in Category Schema
      };
      binderActions[categoryType]?.();
      return;
    }
    const categoryRoutes = {
      [CATEGORY_TYPES.KNOWLEDGE]: ROUTES.KNOWLEDGE_CATEGORIES,
      [CATEGORY_TYPES.VALUES]: ROUTES.LABS_CATEGORIES,
      [CATEGORY_TYPES.CHECKS]: ROUTES.ADMISSION_CATEGORIES,
    };
    const route = categoryRoutes[categoryType];
    if (route) {
      // Check if this is a section - navigate to parent with section hash
      const isSectionItem = isSection(pathParts);
      if (isSectionItem && item.path) {
        const parentId = getParentIdByPath(item.path);
        if (parentId) {
          // Navigate to parent category with hash to expand this section
          navigate(route.replace(':id', parentId) + `#${item.id}`);
        } else {
          // No parent (root-level section) - navigate to main list
          const mainRoutes = {
            [CATEGORY_TYPES.KNOWLEDGE]: ROUTES.KNOWLEDGE,
            [CATEGORY_TYPES.VALUES]: ROUTES.LABS,
            [CATEGORY_TYPES.CHECKS]: ROUTES.ADMISSION,
          };
          const mainRoute = mainRoutes[categoryType];
          if (mainRoute) {
            navigate(mainRoute + `#${item.id}`);
          }
        }
      } else {
        // Regular category/directory - navigate normally
        navigate(route.replace(':id', item.id));
      }
    }
  };

  const renderTagIcon = () => {
    let tagToRender = showSpecialty ? tags?.specialty : null;
    if (!tagToRender) return null;

    return <SingleTagIcon tag={tagToRender} />;
  };

  return (
    <button
      type="button"
      className={classNames([
        'new-design-system',
        styles.root,
        disabled && styles.disabled,
        selected && styles.selected,
        light && styles.light,
      ])}
      onClick={handleClick}
      disabled={disabled}>
      <div className={styles.thumbnailWrapper}>
        <img
          src={Icon}
          className={classNames(
            styles.categoryIcon,
            externalSyncBinderIcon && styles.externalSyncIcon,
          )}
          alt={`${categoryType}-category-item`}
        />
      </div>

      <div className={styles.content}>
        <h4>{title}</h4>
        <div className={styles.labelsContainer}>{showLabels && <LabelsList labels={labels} />}</div>
        <span className={styles.subtitle}>{subtitle}</span>
      </div>

      <div className={styles.rightSection}>
        <div className={styles.tagsContainer}>{renderTagIcon()}</div>
      </div>
    </button>
  );
};

export { CategorySearchItem };
